import { getProp } from "../ParsedSummary";
import { collaborationLevelDescriptions } from "./questionBreakdown";
import { teamworkLevelLabel } from "utils/teamworkLevel";
import { collaborationLevelWording } from "utils/collaborationWording";
import { criticalIncidents, incidentTagCounts } from "utils/taggedQuotes";

export const sessionHeadlines = (summary, sections, sessionMetadata, deterministic = {}) => {
  const headlines = getProp(summary, ['topHeadlines', 'top_headlines', 'headlines']);
  // v9 removed one nesting level: sessionHeadlines.topHeadlines.{...} became
  // sessionHeadlines.{...}. The nested wrapper is used when it is there.
  const topHeadlines = getProp(headlines, ['topHeadlines', 'headlines']) || headlines;
  if (!topHeadlines) return;

  sections.push({
    type: 'title-main',
    content: 'Session Headlines'
  });

  // Add Group Size Recommendation as first bullet if studentCount is < 3 or > 5
  const studentCount = parseInt(sessionMetadata?.studentCount, 10);
  if (!isNaN(studentCount) && (studentCount < 3 || studentCount > 5)) {
    sections.push({
      type: 'bullet',
      label: 'Group Size Recommendation (3-5 Students):',
      content: `This session included ${studentCount} student${studentCount !== 1 ? 's' : ''}. For optimal collaboration and to ensure every student has a chance to share their thinking, we strongly recommend group sizes of 3-5 students.`
    });
  }

  // Focus reads the same title the group code card shows, so a lesson session
  // names its lesson and a multi-standard one names every standard — not the
  // single prefix the report metadata carries.
  const focus = deterministic.focus || sessionMetadata?.teacherSelectedStandard;
  if (focus && typeof focus === 'string') {
    sections.push({
      type: 'bullet',
      label: 'Focus:',
      content: focus
    });
  }

  const headline = getProp(topHeadlines, ['headline', 'atAGlance']);
  if (headline && typeof headline === 'string') {
    sections.push({
      type: 'bullet',
      label: 'At-a-Glance:',
      content: headline
    });
  }

  const recommendedNextSteps = getProp(topHeadlines, ['recommendedNextSteps', 'recommended_next_steps', 'nextSteps']);
  if (recommendedNextSteps && typeof recommendedNextSteps === 'string') {
    // The label is ours now, so a copy the model already wrote into the prose is
    // dropped rather than shown twice. Stored pre-v9 reports open with it
    // verbatim, and they are never regenerated — so without this every teacher
    // reading an old report gets "Recommended Next Steps: Recommended Next
    // Steps: ...", which is worse than the bare prose this replaced.
    const content = recommendedNextSteps.replace(
      /^\s*(?:Recommended\s+)?Next\s+Steps\s*:\s*/i,
      ''
    );
    sections.push({
      type: 'bullet',
      label: 'Recommended Next Steps:',
      // A line that was nothing but the label leaves no sentence behind; keep
      // the original rather than rendering an empty bullet.
      content: content || recommendedNextSteps
    });
  }

  const storedTeamworkLevel = getProp(topHeadlines, ['teamworkLevel', 'teamwork_level', 'teamworkDynamic', 'teamwork_dynamic']);
  if (storedTeamworkLevel && typeof storedTeamworkLevel === 'string') {
    const teamworkLevel = collaborationLevelWording(storedTeamworkLevel);
    // The bolded label comes from the backend's session collaboration score,
    // and only the sentence after it is the LLM's. Without a backend level the
    // whole bullet stays the LLM's, as before.
    const backendLabel = teamworkLevelLabel(deterministic.groupCollabLevel);
    const levelMatch = teamworkLevel.match(/^(.*?Level\s+\d+:\s*[^:]+):\s*(.*)$/i);

    const levelNum = backendLabel
      ? String(deterministic.groupCollabLevel)
      : String(teamworkLevel).match(/\d+/)?.[0];
    const tooltip = levelNum ? collaborationLevelDescriptions[levelNum] : undefined;

    if (backendLabel) {
      sections.push({
        type: 'bullet',
        label: backendLabel,
        // The LLM's own label is dropped, not shown twice; when it wrote no
        // separable sentence its whole line is the content.
        content: levelMatch ? levelMatch[2] : teamworkLevel,
        tooltip
      });
    } else if (levelMatch) {
      sections.push({
        type: 'bullet',
        label: levelMatch[1],
        content: levelMatch[2],
        tooltip
      });
    } else {
      sections.push({
        type: 'bullet',
        content: teamworkLevel,
        tooltip
      });
    }
  }


  const keyHighlight = getProp(topHeadlines, ['keyHighlight', 'key_highlight']);
  if (keyHighlight && typeof keyHighlight === 'string') {
    // Strip "Key Highlight:" from content if it already exists to avoid duplication
    const cleanedContent = keyHighlight.replace(/^key highlight:\s*/i, '');
    sections.push({
      type: 'bullet',
      label: 'Key Highlight:',
      content: cleanedContent
    });
  }

  const studentStar = getProp(topHeadlines, ['studentStar', 'student_star']);
  if (studentStar && typeof studentStar === 'string') {
    const cleanedContent = studentStar.replace(/^student star:\s*/i, '');
    sections.push({
      type: 'bullet',
      label: 'Student Star:',
      content: cleanedContent
    });
  }

  // Anything the tagger flagged for welfare or a hostile climate is raised
  // here, directly under the headlines, so a teacher meets it before the rest
  // of the report. The report shows the rolled-up strip (same block as the
  // lesson report card); the quotes themselves live on the Critical Incidents
  // tab.
  //
  // A flag always surfaces, deterministic rollout or not — it is safety data,
  // not a report number. A session with nothing flagged shows no block at all.
  const counts = incidentTagCounts(criticalIncidents(deterministic.taggedQuotes));
  if (counts.length > 0) {
    sections.push({ type: 'incident-counts', counts });
  }
};
